import { useEffect, useRef } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { Scope } from '../../../enums/scope'
import { isAuthorized } from '../../../utils/isAuthorized'
import { fetchScoutConnections } from '../query-api'
import { ScoutConnection } from '../types/scoutConnection'

interface UseFetchScoutConnectionsResponse {
  connections?: ScoutConnection[]
  isLoading: boolean
}

interface UseFetchScoutConnectionsProps {
  onScoutProcessingEnd?: () => void
  shouldRefetch?: boolean
}

const SYNC_REFETCH_INTERVAL = 10000 // 10 seconds

export const useFetchScoutConnections = (
  props: UseFetchScoutConnectionsProps = {},
): UseFetchScoutConnectionsResponse => {
  const { onScoutProcessingEnd, shouldRefetch = false } = props
  const { organization } = useUserOrganization()
  const prevProcessingStateRef = useRef<boolean | undefined>(undefined)
  const { data: connections, isLoading } = useQuery(
    [QueryKeys.ScoutConnections, organization?.id],
    () => organization && fetchScoutConnections(organization?.id),
    {
      enabled: !!organization?.id && isAuthorized(Scope.ScoutWrite),
      staleTime: defaultStaleTime,
      refetchInterval: shouldRefetch ? SYNC_REFETCH_INTERVAL : false,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
    },
  )

  const isProcessing = connections?.some((connection) => connection.isProcessing)

  useEffect(() => {
    if (isProcessing === false && prevProcessingStateRef.current === true) {
      onScoutProcessingEnd?.()
    }
    prevProcessingStateRef.current = isProcessing
  }, [isProcessing, onScoutProcessingEnd])

  return {
    connections,
    isLoading,
  }
}
